import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import { INBOX_TYPES } from 'shared/mixins/inboxMixin';
import InboxesAPI from '../../api/inboxes';
import InboxMembers from '../../api/inboxMembers';
import WebChannel from '../../api/channel/webChannel';
import FBChannel from '../../api/channel/fbChannel';
import TwilioChannel from '../../api/channel/twilioChannel';
import { throwErrorMessage } from '../utils/api';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { ACCOUNT_EVENTS } from '../../helper/AnalyticsHelper/events';

const isPlainObject = value =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const hasAvatarFile = inboxParams =>
  typeof File !== 'undefined' && inboxParams.avatar instanceof File;

const buildInboxData = inboxParams => {
  const formData = new FormData();
  const { channel = {}, ...inboxProperties } = inboxParams;
  Object.keys(inboxProperties).forEach(key => {
    formData.append(key, inboxProperties[key]);
  });
  const { selectedFeatureFlags, ...channelParams } = channel;
  // selectedFeatureFlags needs to be empty when creating a website channel
  if (selectedFeatureFlags) {
    if (selectedFeatureFlags.length) {
      selectedFeatureFlags.forEach(featureFlag => {
        formData.append(`channel[selected_feature_flags][]`, featureFlag);
      });
    } else {
      formData.append('channel[selected_feature_flags][]', '');
    }
  }
  Object.keys(channelParams).forEach(key => {
    const value = channelParams[key];
    // Plain objects (whatsapp_config) would be flattened to "[object Object]" by
    // FormData. Serializing keeps the value readable on the backend, including the
    // empty object that clears the config.
    formData.append(
      `channel[${key}]`,
      isPlainObject(value) ? JSON.stringify(value) : value
    );
  });
  return formData;
};

export const state = {
  records: [],
  inboxMembers: [],
  myInboxes: [],
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isUpdatingIMAP: false,
    isUpdatingSMTP: false,
    isApplyingBusinessHoursToAll: false,
  },
};

export const getters = {
  getInboxes($state) {
    return $state.records;
  },
  getWhatsAppTemplates: $state => (inboxId) => {
    const [inbox] = $state.records.filter(
      record => record.id === Number(inboxId)
    );
  
    if (!inbox) return [];
  
    try {
      // For API inboxes — templates are stored in inbox.message_templates (not whatsapp_config)
      if (inbox.channel_type === 'Channel::Api') {
        // Mirrors Channel::Api#whatsapp_enabled?: the bot has to be on AND the config has to
        // be there. The templates belong to that WhatsApp setup, so without both there is no
        // way to deliver them and they must not be offered. message_templates keeps the value
        // synced when the bot was on, so a stale record here would still show the button.
        let config = inbox.whatsapp_config;
        if (typeof config === 'string') {
          try {
            config = JSON.parse(config);
          } catch (_e) {
            return [];
          }
        }

        if (!inbox.agent_bot_enabled || !config || !Object.keys(config).length) {
          return [];
        }

        let templates = inbox.message_templates;
        if (typeof templates === 'string') {
          try {
            templates = JSON.parse(templates);
          } catch (_e) {
            return [];
          }
        }

        if (!Array.isArray(templates) || !templates.length) {
          return [];
        }

        // Return only the default template if one is pinned
        if (config.default_template_id) {
          return templates.filter(
            template => template.id === config.default_template_id
          );
        }

        // Return all approved templates for API inboxes
        return templates.filter(
          template => template.status?.toLowerCase() === 'approved'
        );
      }
  
      // For WhatsApp and ZapiGroups inboxes — same template filtering rules
      let templates = [];
      if (typeof inbox.message_templates === 'string') {
        try {
          templates = JSON.parse(inbox.message_templates);
        } catch (parseError) {
          return [];
        }
      } else {
        templates = inbox.message_templates;
      }

      if (!Array.isArray(templates)) {
        return [];
      }

      return templates.filter(template => {
        return template.status?.toLowerCase() === 'approved' &&
          template.components?.every(component => {
            return !['DOCUMENT', 'IMAGE', 'VIDEO'].includes(component.format);
          });
      });
    } catch (error) {
      console.error('Error in getWhatsAppTemplates:', error);
      return [];
    }
  },
  myInboxes($state) {
    return $state.myInboxes;
  },
  getMyInboxesNames($state) {
    return $state.myInboxes.map(inbox => inbox.name.toLowerCase());
  },
  records($state) {
    return $state.records;
  },
  getNewConversationInboxes($state) {
    return $state.records.filter(inbox => {
      const { channel_type: channelType, phone_number: phoneNumber = '' } =
        inbox;

      const isEmailChannel = channelType === INBOX_TYPES.EMAIL;
      const isSmsChannel =
        channelType === INBOX_TYPES.TWILIO &&
        phoneNumber.startsWith('whatsapp');
      return isEmailChannel || isSmsChannel;
    });
  },
  getInbox: $state => inboxId => {
    // Return early if inboxId is null or undefined
    if (inboxId === null || inboxId === undefined) {
      return {};
    }
  
    const [inbox] = $state.records.filter(
      record => record.id === Number(inboxId)
    );
    
    if (inbox) {
      return inbox;
    }
  
    return InboxesAPI.show(inboxId)
      .then(response => response.data)
      .catch(() => ({}));
  },  
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getWebsiteInboxes($state) {
    return $state.records.filter(item => item.channel_type === INBOX_TYPES.WEB);
  },
  getTwilioInboxes($state) {
    return $state.records.filter(
      item => item.channel_type === INBOX_TYPES.TWILIO
    );
  },
  getSMSInboxes($state) {
    return $state.records.filter(
      item =>
        item.channel_type === INBOX_TYPES.SMS ||
        (item.channel_type === INBOX_TYPES.TWILIO && item.medium === 'sms')
    );
  },
  dialogFlowEnabledInboxes($state) {
    return $state.records.filter(
      item => item.channel_type !== INBOX_TYPES.EMAIL
    );
  },
};

const sendAnalyticsEvent = channelType => {
  AnalyticsHelper.track(ACCOUNT_EVENTS.ADDED_AN_INBOX, {
    channelType,
  });
};

export const actions = {
  revalidate: async ({ commit }, { newKey }) => {
    try {
      const isExistingKeyValid = await InboxesAPI.validateCacheKey(newKey);
      if (!isExistingKeyValid) {
        const response = await InboxesAPI.refetchAndCommit(newKey);
        commit(types.default.SET_INBOXES, response.data.payload);
      }
    } catch (error) {
      // Ignore error
    }
  },
  get: async ({ commit }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: true });
    try {
      const response = await InboxesAPI.get(true);
      
      // Garante que os templates estão presentes nos dados
      const inboxes = response.data.payload.map(inbox => ({
        ...inbox,
        message_templates: inbox.message_templates || [],
      }));
      
      commit(types.default.SET_INBOXES, inboxes);
      commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: false });
    } catch (error) {
      console.error('Error fetching inboxes:', error);
      commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: false });
    }
  },
  getInboxMembers: async ({ commit }, { inboxIds }) => {
    try {
      const promises = inboxIds.map(inboxId => InboxMembers.get(inboxId));
      const responses = await Promise.all(promises);
      const members = responses.flatMap(response => response.data);

      commit(types.default.SET_INBOX_MEMBERS, members);
    } catch (error) {
      throw new Error(error);
    }
  },
  fetchMyInboxes: async ({ commit, rootGetters, rootState }, agentId) => {
    const currentUserId = agentId ? agentId : rootGetters.getCurrentUser?.id;
    if (!currentUserId) {
      return;
    }
    try {
      const response = await InboxMembers.getByUserId(currentUserId);
      const userInboxMembers = response.data;
      const userInboxIds = userInboxMembers.map(member => member.id);
      const myInboxes = rootState.inboxes.records.filter(inbox =>
        userInboxIds.includes(inbox.id)
      );
      commit('setMyInboxes', myInboxes);
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  },
  createChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await WebChannel.create(params);
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      const { channel = {} } = params;
      sendAnalyticsEvent(channel.type);
      return response.data;
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      throw new Error(errorMessage);
    }
  },
  createWebsiteChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await WebChannel.create(buildInboxData(params));
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      sendAnalyticsEvent('website');
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      return throwErrorMessage(error);
    }
  },
  createTwilioChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await TwilioChannel.create(params);
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      sendAnalyticsEvent('twilio');
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      throw new Error(error);
    }
  },
  createFBChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await FBChannel.create(params);
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      sendAnalyticsEvent('facebook');
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      throw new Error(error);
    }
  },
  updateInbox: async ({ commit }, { id, formData = true, ...inboxParams }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isUpdating: true });
    try {
      // whatsapp_config is a nested object, so it travels as a regular JSON payload.
      // An avatar file still requires multipart — buildInboxData serializes the config
      // in that case so it is not lost.
      if (
        inboxParams.channel?.whatsapp_config !== undefined &&
        !hasAvatarFile(inboxParams)
      ) {
        const response = await InboxesAPI.update(id, inboxParams);
        commit(types.default.EDIT_INBOXES, response.data);
        commit(types.default.SET_INBOXES_UI_FLAG, { isUpdating: false });
        return;
      }

      // Otherwise use the existing FormData approach
      const response = await InboxesAPI.update(
        id,
        formData ? buildInboxData(inboxParams) : inboxParams
      );
      commit(types.default.EDIT_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdating: false });
      throwErrorMessage(error);
    }
  },
  // Applies the business hours of the source inbox to every eligible inbox of the account.
  // Returns the structured result of the operation, the caller renders the feedback from it and
  // never derives success or failure on its own.
  applyBusinessHoursToAllInboxes: async (
    { commit, dispatch },
    { id, ...payload }
  ) => {
    commit(types.default.SET_INBOXES_UI_FLAG, {
      isApplyingBusinessHoursToAll: true,
    });
    try {
      const response = await InboxesAPI.applyBusinessHoursToAllInboxes(
        id,
        payload
      );
      // Every inbox of the account may have changed, the local records are refetched. A failure
      // here must not be reported as a failure of the write the backend already persisted.
      try {
        await dispatch('get');
      } catch (refreshError) {
        // the stale list is corrected on the next navigation
      }
      return response.data;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.default.SET_INBOXES_UI_FLAG, {
        isApplyingBusinessHoursToAll: false,
      });
    }
  },
  updateInboxIMAP: async ({ commit }, { id, ...inboxParams }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingIMAP: true });
    try {
      const response = await InboxesAPI.update(id, inboxParams);
      commit(types.default.EDIT_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingIMAP: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingIMAP: false });
      throwErrorMessage(error);
    }
  },
  updateInboxSMTP: async ({ commit }, { id, ...inboxParams }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingSMTP: true });
    try {
      const response = await InboxesAPI.update(id, inboxParams);
      commit(types.default.EDIT_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingSMTP: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingSMTP: false });
      throwErrorMessage(error);
    }
  },
  delete: async ({ commit }, inboxId) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isDeleting: true });
    try {
      await InboxesAPI.delete(inboxId);
      commit(types.default.DELETE_INBOXES, inboxId);
      commit(types.default.SET_INBOXES_UI_FLAG, { isDeleting: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isDeleting: false });
      throw new Error(error);
    }
  },
  reauthorizeFacebookPage: async ({ commit }, params) => {
    try {
      const response = await FBChannel.reauthorizeFacebookPage(params);
      commit(types.default.EDIT_INBOXES, response.data);
    } catch (error) {
      throw new Error(error.message);
    }
  },
  deleteInboxAvatar: async (_, inboxId) => {
    try {
      await InboxesAPI.deleteInboxAvatar(inboxId);
    } catch (error) {
      throw new Error(error);
    }
  },
  async fetchWhatsAppTemplates({ commit }, { accountId, inboxId, credentials }) {
    try {
      const response = await InboxesAPI.fetchWhatsAppTemplates(
        accountId, 
        inboxId, 
        credentials
      );
      return response.data.templates;
    } catch (error) {
      throw new Error(error);
    }
  },
  getAgentInboxes: async ({ commit }, agentId) => {
    try {
      const response = await InboxesAPI.getAgentInboxes(agentId);
      return response;
    } catch (error) {
      throw new Error(error);
    }
  },
};

export const mutations = {
  [types.default.SET_INBOXES_UI_FLAG]($state, uiFlag) {
    $state.uiFlags = { ...$state.uiFlags, ...uiFlag };
  },
  [types.default.SET_INBOXES]: MutationHelpers.set,
  [types.default.SET_INBOXES_ITEM]: MutationHelpers.setSingleRecord,
  [types.default.ADD_INBOXES]: MutationHelpers.create,
  [types.default.EDIT_INBOXES]: MutationHelpers.update,
  [types.default.DELETE_INBOXES]: MutationHelpers.destroy,
  [types.default.SET_INBOX_MEMBERS]($state, inboxMembers) {
    $state.inboxMembers = inboxMembers;
  },
  setMyInboxes($state, inboxes) {
    $state.myInboxes = inboxes;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};